import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import store from "./store";
import { Provider } from "react-redux";
import { SidebarProvider } from "./context/SidebarContext";
import AppErrorBoundary from "./components/Common/ErrorBoundary/AppErrorBoundary";
import { setAlert } from "./action/alert";
import customConfig from "./config";

const normalizeError = (reason) => {
  if (!reason) return {};
  if (typeof reason === "string") {
    return { message: reason };
  }
  return reason;
};

const isTimeoutOrNetworkError = (reason) => {
  const err = normalizeError(reason);
  const isCancelled = err === "REQUEST_CANCELLED" || err?.message === "REQUEST_CANCELLED";
  const name = err?.name?.toLowerCase?.() || "";
  const message = err?.message?.toLowerCase?.() || "";
  const isTimeout =
    err?.isTimeout ||
    err?.code === "ECONNABORTED" ||
    name.includes("timeout") ||
    message.includes("timeout") ||
    message.includes("timed out");
  const isNetwork =
    err?.isNetworkError ||
    message === "network error" ||
    (!err?.response && !isTimeout);

  return { err, isTimeout, isNetwork, isCancelled };
};

const handleGlobalTimeoutOrNetworkError = (reason, event) => {
  try {
    const { err, isTimeout, isNetwork, isCancelled } = isTimeoutOrNetworkError(reason);
    if (!(isTimeout || isNetwork || isCancelled)) {
      return false;
    }

    if (event?.preventDefault) event.preventDefault();
    if (event?.stopImmediatePropagation) event.stopImmediatePropagation();

    if (!isCancelled) {
      const msg = err?.userMessage ?? (isTimeout ? customConfig.TIMEOUT_ERROR : customConfig.NETWORK_ERROR);
      store.dispatch(setAlert(msg, "danger"));
    }

    return true;
  } catch (e) {
    return false;
  }
};

// Handle global errors (timeouts/network failures) by showing a friendly message instead of the default error overlay.
// Using addEventListener avoids the risk of duplicate handling or interfering with other handlers.
window.addEventListener("unhandledrejection", (event) => {
  handleGlobalTimeoutOrNetworkError(event?.reason, event);
}, true);

window.addEventListener("error", (event) => {
  handleGlobalTimeoutOrNetworkError(event?.error || event?.message, event);
}, true);

const container = document.getElementById("root");
const root = createRoot(container);

// NOTE: React.StrictMode is intentionally not used here because some Material UI v4 components
// (and their dependencies) can behave incorrectly under StrictMode (double-mount effects,
// duplicate API calls, or other warnings). Once we complete the migration to MUI v5, we should
// re-enable StrictMode to catch additional issues early.
root.render(
  <Provider store={store}>
    <SidebarProvider>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </SidebarProvider>
  </Provider>
);

